import { CommandExecutionContext, SelfContainedCommand } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { createFileLogger } from '../../../utils/logger';
import { googleDriveAdapter } from '../../../services/integrations/adapters/GoogleDriveAdapter';
import { resolveGoogleDriveIntegration } from '../../../services/integrations/utils/integrationResolver';

const log = createFileLogger('GOOGLE_SHEET_WRITE');

export const googleSheetWriteCommand: SelfContainedCommand = {
  id: 'google_sheet_write',
  description: `Write data to a Google Sheet

Updates cells in the specified range using A1 notation.

**Parameters:**
- spreadsheetId: Google Sheets spreadsheet ID (from URL)
- range: A1 notation range (e.g., "Sheet1!A1:B10")
- values: 2D array of cell values (array of arrays)
- integrationId (optional): Integration ID (auto-detected if omitted)

**Value Input:**
Values are parsed as if typed by user (USER_ENTERED mode):
- Numbers: "123" → 123
- Formulas: "=SUM(A1:A10)" → Evaluated formula
- Dates: "1/1/2024" → Formatted date
- Text: "Hello" → Text string

**Examples:**
1. Write single row:
   values: [["Name", "Age", "City"]]

2. Write multiple rows:
   values: [["Alice", 30, "NYC"], ["Bob", 25, "LA"]]

3. Write formula:
   values: [["=SUM(A1:A10)"]]

**Output:**
Returns updated range and cell count.`,
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'spreadsheetId',
        type: 'string',
        description: 'Google Sheets spreadsheet ID from URL',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Spreadsheet ID cannot be empty';
          return null;
        }
      },
      {
        name: 'range',
        type: 'string',
        description: 'A1 notation range (e.g., "Sheet1!A1:B10")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Range cannot be empty';
          return null;
        }
      },
      {
        name: 'values',
        type: 'array',
        description: '2D array of cell values (array of arrays)',
        required: true,
        validator: (value: any) => {
          if (!Array.isArray(value)) return 'Values must be an array';
          if (value.length === 0) return 'Values array cannot be empty';
          if (!Array.isArray(value[0])) return 'Values must be a 2D array (array of arrays)';
          return null;
        }
      },
      {
        name: 'integrationId',
        type: 'string',
        description: 'Integration ID (auto-detected if omitted)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          spreadsheetId: '1ABC123xyz',
          range: 'Sheet1!A1:B2',
          values: [['Name', 'Age'], ['Alice', 30]]
        },
        expectedOutput: createSuccessResult('Successfully updated Google Sheet', {
          updated_cells: 4,
          success: true
        }),
        description: 'Write data to a Google Sheet',
        context: 'When you need to update spreadsheet cells'
      }
    ],
    bad: [
      {
        input: { spreadsheetId: '1ABC123xyz', range: 'Sheet1!A1', values: [] },
        error: 'Values array cannot be empty',
        why: 'Empty values array',
        fix: 'Provide at least one row of data'
      }
    ]
  },
  
  performance: {
    expectedDuration: 3000,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      let { spreadsheetId, range, values, integrationId } = params;
      
      if (!spreadsheetId) {
        return createErrorResult('Missing spreadsheet ID', 'spreadsheetId parameter is required', 'validation');
      }
      
      if (!range) {
        return createErrorResult('Missing range', 'range parameter is required', 'validation');
      }
      
      // Check if spreadsheetId is an llmId (entity reference) and resolve to actual Drive ID
      if (spreadsheetId.includes('_')) {
        try {
          const useEntityStore = (await import('../../../stores/entityStore')).default;
          const entity = useEntityStore.getState().entities.find((e: any) => e.llmId === spreadsheetId);
          
          if (entity && entity.skeleton?.driveFileId) {
            log.log('[google_sheet_write] Resolved llmId to driveFileId:', { llmId: spreadsheetId, driveFileId: entity.skeleton.driveFileId });
            spreadsheetId = entity.skeleton.driveFileId;
          }
        } catch (error) {
          log.error('[google_sheet_write] Error resolving llmId:', error);
        }
      }
      
      if (!values || !Array.isArray(values) || values.length === 0) {
        return createErrorResult(
          'Invalid values',
          'values must be a non-empty 2D array',
          'validation'
        );
      }
      
      // Validate 2D array structure
      if (!Array.isArray(values[0])) {
        return createErrorResult(
          'Invalid values format',
          'values must be a 2D array (array of arrays)',
          'validation'
        );
      }
      
      // TODO: Same issue as google_sheet_read - AI may guess sheet names (e.g., "Sheet1!A1:B10")
      // that don't exist, causing initial failures. See google_sheet_read.ts for potential solutions.
      
      log.log('[google_sheet_write] Writing to Google Sheet:', {
        spreadsheetId,
        range,
        rows: values.length,
        columns: values[0]?.length || 0,
        hasIntegrationId: !!integrationId
      });
      
      // Resolve integration ID if not provided
      let resolvedIntegrationId = integrationId;
      if (!resolvedIntegrationId) {
        try {
          resolvedIntegrationId = await resolveGoogleDriveIntegration(context.userId);
          log.log('[google_sheet_write] Auto-resolved integration ID:', resolvedIntegrationId);
        } catch (error) {
          return createErrorResult(
            'No Google Drive integration found',
            error instanceof Error ? error.message : 'Please connect Google Drive in Settings → Integrations',
            'validation'
          );
        }
      }
      
      // Write to sheet via adapter
      const result = await googleDriveAdapter.writeGoogleSheet(
        resolvedIntegrationId,
        spreadsheetId,
        range,
        values
      );
      
      log.log('[google_sheet_write] Successfully wrote to sheet:', {
        updatedRange: result.updated_range,
        updatedCells: result.updated_cells
      });
      
      return createSuccessResult(
        result.message || `Successfully updated ${result.updated_cells} cells in Google Sheet`,
        {
          spreadsheet_id: result.spreadsheet_id,
          updated_range: result.updated_range,
          updated_rows: result.updated_rows,
          updated_columns: result.updated_columns,
          updated_cells: result.updated_cells,
          success: result.success
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'google_sheet_write'
          } as any
        }
      );
      
    } catch (error) {
      log.error('[google_sheet_write] Error writing to Google Sheet:', error);
      return createErrorResult(
        'Failed to write to Google Sheet',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

